import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import Select from 'react-select';
import { useAuth } from '../AuthContext';
import Alerty from '../../common/Alerty';
import { fetchStaffMembers, staffOptions } from './accountManagers';
const { rootUrl } = require('../../../config/config');
const initialNewCarState = {
  plate_no: '',
  model: '',
  make: '',
  insurer: '',
  insurer_expiry: '',
  year: '',
  status: '',
  drive_link: '',
  driver_name: '',
  driver_id: '',
  owner_code: '',
  owner_name: '',
  account_manager_id: '',
  is_discounted: false,
  usage_type: 'BUSINESS',
  input_by: '',
  input_date: '',
  edit_by: '',
  edit_date: ''
};

const USAGE_TYPE_OPTIONS = [
  { value: 'BUSINESS', label: 'Business' },
  { value: 'PRIVATE', label: 'Private' }
];

const CarForm = ({ setShowAddCarForm, editingCar = null, setEditingCar, fetchCars }) => {

  const { token, user } = useAuth();
  const [alert, setAlert] = useState({ color: '', message: '' });
  const [inAlert, setInAlert] = useState(false);
  const [errors, setErrors] = useState({ plate_no: '' });
  const [owners, setOwners] = useState([]);

  const [carsStatus, setCarsStatus] = useState([]);
  const [staff, setStaff] = useState([]);
  const [staffError, setStaffError] = useState('');

  useEffect(() => {
    let cancelled = false;
    fetchStaffMembers(token)
      .then((rows) => { if (!cancelled) setStaff(rows); })
      .catch(() => { if (!cancelled) setStaffError('Could not load staff members. Close the form and try again.'); });
    return () => { cancelled = true; };
  }, [token]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [carsResponse, ownersResponse] = await Promise.all([
          axios.get(`${rootUrl}/cars/status`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(`${rootUrl}/owners/list`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);

        setCarsStatus(carsResponse.data.data);
        setOwners(ownersResponse.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    console.log(editingCar)
    fetchData();
  }, []);

  useEffect(() => {
    if (alert.message) {
      setInAlert(true);
      const timer = setTimeout(() => {
        setInAlert(false);
        setAlert({ color: '', message: '' });
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [alert]);

  // Close when pressing Escape
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        setShowAddCarForm(false);
        if (setEditingCar) setEditingCar(null);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [setShowAddCarForm, setEditingCar]);


  const [newCar, setNewCar] = useState(editingCar == null ? initialNewCarState : editingCar);

  // Update newCar when editingCar changes
  useEffect(() => {
    if (editingCar) {
      // Fetch fresh car data when editing
      const fetchCarData = async () => {
        try {
          const response = await axios.get(`${rootUrl}/cars/${editingCar.plate_no}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          setNewCar(response.data);
        } catch (error) {
          console.error('Error fetching car data:', error);
          // Fallback to the passed editingCar if fetch fails
          setNewCar(editingCar);
        }
      };
      fetchCarData();
    } else {
      setNewCar(initialNewCarState);
    }
  }, [editingCar, token]);

  const handleAddCar = async () => {
    let newErrors = {};

    if (!newCar.plate_no || newCar.plate_no.trim() === '') newErrors.plate_no = 'Plate Number is Required!';
    if (!newCar.model || newCar.model.trim() === '') newErrors.model = 'Car Model is Required!';
    if (!newCar.make || newCar.make.trim() === '') newErrors.make = 'Car Make is Required!';
    if (!newCar.year || newCar.year.toString().trim() === '') newErrors.year = 'Year Make is Required!';
    if (!newCar.owner_code || newCar.owner_code.toString().trim() === "") {
      newErrors.owner_code = 'Owner is Required!';
    }
    if (!newCar.account_manager_id) {
      newErrors.account_manager_id = 'Account Manager is Required! Pick the staff member responsible for this car.';
    }

    // Status validation - ensure it's a valid numeric ID
    if (!newCar.status || newCar.status.toString().trim() === '') {
      const defaultStatus = carsStatus.find(status => status.def_status === true);
      if (defaultStatus) {
        newCar.status = defaultStatus.id;
      } else {
        newErrors.status = 'Car Status is Required!';
      }
    } else {
      // Ensure status is a numeric value
      const statusId = parseInt(newCar.status, 10);
      if (isNaN(statusId)) {
        newErrors.status = 'Invalid car status';
      } else {
        newCar.status = statusId;
      }
    }

    console.log('Validation errors:', newErrors);
    if (Object.keys(newErrors).length > 0) {
      setAlert({ message: 'Check out the following errors to continue!', color: 'orange' });
      setInAlert(true);
      setErrors(newErrors);
      return false;
    }


    try {

      const carData = {
        plate_no: newCar.plate_no,
        model: newCar.model,
        make: newCar.make,
        year: newCar.year,
        status: newCar.status,
        owner_code: newCar.owner_code,
        account_manager_id: Number(newCar.account_manager_id),
        is_discounted: !!newCar.is_discounted,
        usage_type: newCar.usage_type === 'PRIVATE' ? 'PRIVATE' : 'BUSINESS',
        ...(editingCar ? { edit_by: user.username } : { input_by: user.username })
      };


      const response = editingCar
        ? await axios.put(
          `${rootUrl}/cars/${editingCar.plate_no}`,
          carData,
          {
            validateStatus: status => [200, 201, 400, 422, 409].includes(status),
          }
        )
        : await axios.post(
          `${rootUrl}/cars`,
          carData,
          {
            validateStatus: status => [200, 201, 400, 422, 409].includes(status),
          }
        );

      if (response.data.errors) {
        console.log(response)
      }

      if (response.status === 201 || response.status === 200) {
        setShowAddCarForm(false);
        setAlert({ message: editingCar ? 'Car updated successfully!' : 'Car added successfully!', color: 'green' });
        setInAlert(true)
        fetchCars();
        setNewCar(initialNewCarState);
        setEditingCar(null);
      } else {
        console.error("Unexpected server response status:", response.data);
        // Handle different error response formats
        const errorMessage = response.data.error || response.data.message || 'An unexpected error occurred';
        setAlert({ color: 'red', message: errorMessage });
        setInAlert(true)
      }
    } catch (error) {
      console.error("Error during axios request:", error);
      // Handle different error response formats
      const errorMessage = error.response?.data?.error ||
        error.response?.data?.message ||
        error.message ||
        'An unexpected error occurred';
      setAlert({ color: 'red', message: errorMessage });
      setInAlert(true)
    }
  };





  const overlayRef = useRef(null);

  const handleOverlayClick = (e) => {
    // only close when clicking directly on the overlay (outside the modal)
    if (e.target === e.currentTarget) {
      setShowAddCarForm(false);
      if (setEditingCar) setEditingCar(null);
    }
  };

  return (
    <div ref={overlayRef} onClick={handleOverlayClick} className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white w-full max-w-sm sm:max-w-2xl rounded-lg shadow-lg p-6 mx-4" role="dialog" aria-modal="true">
        <h2 className="text-2xl font-semibold text-gray-800 mb-6">
          {editingCar ? `Editing Car: ${editingCar.plate_no}` : 'Add Car'}
        </h2>

        {inAlert && (
          <div className="mb-6">
            <Alerty open={inAlert} setOpen={setInAlert} message={alert.message} color={alert.color} />
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="flex flex-col">
            <label htmlFor="plate_no" className="text-sm font-medium text-gray-700 mb-1">
              Plate Number
            </label>
            {editingCar ? (
              <span className="px-3 py-2 bg-gray-200 text-gray-700 rounded-md">
                {editingCar.plate_no}
              </span>
            ) : (
              <input
                id="plate_no"
                type="text"
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={newCar.plate_no}
                onChange={(e) => {
                  const plate_noValue = e.target.value.toUpperCase();
                  if (plate_noValue.startsWith('K')) {
                    if (/^[A-Z0-9\s]*$/.test(plate_noValue) && plate_noValue.length <= 7) {
                      setNewCar({ ...newCar, plate_no: plate_noValue });
                      if (plate_noValue.length === 7) {
                        setErrors((prev) => ({ ...prev, plate_no: '' }));
                      }
                    }
                  } else {
                    setErrors((prev) => ({
                      ...prev,
                      plate_no: 'Plate no start with "K" & must be 7 character'
                    }));
                  }
                }}
              />
            )}
            {errors.plate_no && <p className="mt-2 text-sm text-red-500">{errors.plate_no}</p>}
          </div>

          <div className="flex flex-col">
            <label htmlFor="model" className="text-sm font-medium text-gray-700 mb-1">
              Model
            </label>
            <input
              id="model"
              type="text"
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={newCar.model}
              onChange={(e) => setNewCar({ ...newCar, model: e.target.value })}
            />
            {errors.model && <p className="mt-2 text-sm text-red-500">{errors.model}</p>}
          </div>

          <div className="flex flex-col">
            <label htmlFor="make" className="text-sm font-medium text-gray-700 mb-1">
              Make
            </label>
            <input
              id="make"
              type="text"
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={newCar.make}
              onChange={(e) => setNewCar({ ...newCar, make: e.target.value })}
            />
            {errors.make && <p className="mt-2 text-sm text-red-500">{errors.make}</p>}
          </div>

          <div className="flex flex-col">
            <label htmlFor="year" className="text-sm font-medium text-gray-700 mb-1">
              Year of Make
            </label>
            <input
              id="year"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={newCar.year}
              onChange={(e) => {
                const numericValue = e.target.value.replace(/\D/g, '').slice(0, 4);
                setNewCar(prev => ({ ...prev, year: numericValue }));
              }}
            />
            {errors.year && <p className="mt-2 text-sm text-red-500">{errors.year}</p>}
          </div>

          <div className="flex flex-col">
            <label htmlFor="status" className="text-sm font-medium text-gray-700 mb-1">
              Status <a href="/cars/status" target="_blank" rel="noopener noreferrer" className="ml-2 text-xs text-blue-600 hover:underline">Add New Status</a>
            </label>

            <Select
              id="status"
              value={carsStatus.find(s => s.id === newCar.status) ? { value: newCar.status, label: carsStatus.find(s => s.id === newCar.status).name } : null}
              options={carsStatus.map(statusObj => ({
                value: statusObj.id,
                label: statusObj.name
              }))}
              onChange={(option) => setNewCar({ ...newCar, status: option?.value || '' })}
              placeholder="Select Status"
              isClearable
              styles={{
                control: (base) => ({
                  ...base,
                  backgroundColor: '#f5f5f5',
                  borderColor: '#d1d5db',
                  '&:hover': { borderColor: '#9ca3af' }
                }),
                option: (base, state) => ({
                  ...base,
                  backgroundColor: state.isSelected ? '#FF5733' : state.isFocused ? '#f3f4f6' : 'white',
                  color: state.isSelected ? 'white' : 'black',
                  cursor: 'pointer'
                })
              }}
            />

            {errors.status && <p className="mt-2 text-sm text-red-500">{errors.status}</p>}
          </div>

          <div className="flex flex-col">
            <label htmlFor="owner" className="text-sm font-medium text-gray-700 mb-1">
              Owner <a href="/owners" target="_blank" rel="noopener noreferrer" className="ml-2 text-xs text-blue-600 hover:underline">Add New Owner</a>
            </label>
            <Select
              id="owner"
              value={owners.find(o => o.code === newCar.owner_code) ? { value: newCar.owner_code, label: owners.find(o => o.code === newCar.owner_code).name } : null}
              options={owners.map(owner => ({
                value: owner.code,
                label: owner.name
              }))}
              onChange={(option) => {
                const selectedOwner = owners.find((o) => o.code === option?.value);
                setNewCar({
                  ...newCar,
                  owner_code: option?.value || '',
                  owner_name: selectedOwner ? selectedOwner.name : '',
                });
              }}
              placeholder="Select Owner"
              isClearable
              styles={{
                control: (base) => ({
                  ...base,
                  backgroundColor: '#f5f5f5',
                  borderColor: '#d1d5db',
                  '&:hover': { borderColor: '#9ca3af' }
                }),
                option: (base, state) => ({
                  ...base,
                  backgroundColor: state.isSelected ? '#FF5733' : state.isFocused ? '#f3f4f6' : 'white',
                  color: state.isSelected ? 'white' : 'black',
                  cursor: 'pointer'
                })
              }}
            />
            {errors.owner_code && (
              <p className="mt-2 text-sm text-red-500">{errors.owner_code}</p>
            )}
          </div>

          <div className="flex flex-col">
            <label htmlFor="account_manager" className="text-sm font-medium text-gray-700 mb-1">
              Account Manager <span className="text-red-500">*</span>
            </label>
            <Select
              inputId="account_manager"
              value={staffOptions(staff).find(o => String(o.value) === String(newCar.account_manager_id)) || null}
              options={staffOptions(staff)}
              onChange={(option) => {
                setNewCar(prev => ({ ...prev, account_manager_id: option?.value || '' }));
                if (option) setErrors(prev => ({ ...prev, account_manager_id: '' }));
              }}
              placeholder="Select Account Manager"
              isClearable
              styles={{
                control: (base) => ({
                  ...base,
                  backgroundColor: '#f5f5f5',
                  borderColor: '#d1d5db',
                  '&:hover': { borderColor: '#9ca3af' }
                }),
                option: (base, state) => ({
                  ...base,
                  backgroundColor: state.isSelected ? '#FF5733' : state.isFocused ? '#f3f4f6' : 'white',
                  color: state.isSelected ? 'white' : 'black',
                  cursor: 'pointer'
                })
              }}
            />
            {(errors.account_manager_id || staffError) && (
              <p className="mt-2 text-sm text-red-500">{errors.account_manager_id || staffError}</p>
            )}
          </div>

          <div className="flex flex-col">
            <label htmlFor="usage_type" className="text-sm font-medium text-gray-700 mb-1">
              Car Type
            </label>
            <Select
              inputId="usage_type"
              value={USAGE_TYPE_OPTIONS.find(o => o.value === (newCar.usage_type || 'BUSINESS'))}
              options={USAGE_TYPE_OPTIONS}
              onChange={(option) => {
                setNewCar(prev => ({
                  ...prev,
                  usage_type: option?.value || 'BUSINESS'
                }));
              }}
              isSearchable={false}
              styles={{
                control: (base) => ({
                  ...base,
                  backgroundColor: '#f5f5f5',
                  borderColor: '#d1d5db',
                  '&:hover': { borderColor: '#9ca3af' }
                }),
                option: (base, state) => ({
                  ...base,
                  backgroundColor: state.isSelected ? '#FF5733' : state.isFocused ? '#f3f4f6' : 'white',
                  color: state.isSelected ? 'white' : 'black',
                  cursor: 'pointer'
                })
              }}
            />
            {newCar.usage_type === 'PRIVATE' && (
              <p className="mt-2 text-sm text-gray-500">
                Private cars are excluded from weekly billing, owner invoices and remittance.
              </p>
            )}
          </div>

          <div className="bg-amber-50 p-4 rounded-lg border border-amber-200 md:col-span-2">
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="is_discounted"
                name="is_discounted"
                checked={Boolean(newCar.is_discounted)}
                onChange={(e) => {
                  setNewCar(prev => ({
                    ...prev,
                    is_discounted: e.target.checked
                  }));
                }}
                className="w-4 h-4 cursor-pointer"
              />
              <label htmlFor="is_discounted" className="cursor-pointer">
                <p className="text-sm font-medium text-gray-700">Fully Discounted</p>
                <p className="text-sm text-gray-500">
                  Exclude this car from owner invoicing.
                </p>
              </label>
            </div>
          </div>

          <div className="flex flex-col md:col-span-2">
            <label htmlFor="drive_link" className="text-sm font-medium text-gray-700 mb-1">
              Drive Folder
            </label>
            <div className="w-full border border-gray-300 rounded-md px-3 py-2 bg-gray-50 text-sm text-gray-600">
              {newCar.drive_link ? (
                <a href={newCar.drive_link} target="_blank" rel="noreferrer noopener" className="text-blue-600 hover:underline break-all">
                  Open auto-generated Drive folder
                </a>
              ) : (
                <span>Auto-generated after save</span>
              )}
            </div>
            <p className="mt-2 text-xs text-gray-500">
              This folder is created automatically and cannot be edited manually.
            </p>
          </div>

          <div className="md:col-span-2 flex justify-end space-x-4 mt-6">
            <button
              onClick={handleAddCar}
              className="bg-[#FF5733] hover:bg-[#e64520] text-white text-sm font-semibold rounded-sm px-4 py-2 shadow-none"
            >
              {editingCar ? 'Update' : 'Add'}
            </button>
            <button
              onClick={() => { setShowAddCarForm(false); setEditingCar(null); }}
              className="bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-semibold rounded-sm px-4 py-2 shadow-none"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CarForm;
